import { http } from 'utils'
import {pick, concat, set, findIndex} from 'lodash'

// Payment mutation imports.
import { ADD_PAYMENTS, SET_FILTER, RESET_FILTERS, SET_PAYMENTS, SET_PAYMENTS_PAGINATION, SET_PAYMENT_CLIENT_TOKEN, SET_PAYMENT_LIST_PAYMENT } from 'vx/mutations'

// Payment state.
export const initialPaymentFiltersState = () => ({
  locations: [],
  opportunities: [],
  regions: [],
  status: ['unpaid', 'overdue'],
  amount_low: null,
  amount_high: null,
  date_low: null,
  date_high: null
})

const state = {
  clientToken: null,
  payments: [],
  pagination: {},
  filters: initialPaymentFiltersState()
}

// Payment state getters.
const getters = {
  /**
   * Initial payment filters state.
   *
   * @return obejct
   */
  initialFiltersState () {
    return initialPaymentFiltersState()
  },

  /**
   * Determine if we have no more payments to load.
   *
   * @return bool
   */
  haveNoMorePayments: (state) => {
    return state.pagination.current_page >= state.pagination.last_page ||
      state.pagination.total === 0
  }
}

// Payment actions.
const actions = {
  /**
   * Get the payments.
   *
   * @return Promise
   */
  getPayments ({ commit }, params = {}) {
    return http.get('payments', { params })
      .then((response) => {
        commit(SET_PAYMENTS, response.data.data)
        commit(SET_PAYMENTS_PAGINATION, response.data)

        return response
      })
  },

  /**
   * Get next set of paginated payments.
   *
   * @return Promise
   */
  getNextPayments ({ commit, state, getters }, params = {}) {
    if (getters.haveNoMorePayments) {
      return Promise.reject(new Error('There are no more payments to load.'))
    }

    params.page = state.pagination.current_page + 1

    return http.get(`payments`, { params })
      .then((response) => {
        commit(ADD_PAYMENTS, response.data.data)
        commit(SET_PAYMENTS_PAGINATION, response.data)

        return response
      })
  },

  /**
   * Update payments as sent.
   *
   * @return Promise
   */
  updatePaymentSent ({ commit }, { bookingId, payload }) {
    return http.put(`bookings/${bookingId}/payment_sent`, payload)
  },

  /**
   * Update payments as received.
   *
   * @return Promise
   */
  updatePaymentReceived ({ commit }, { bookingId, payload }) {
    return http.put(`bookings/${bookingId}/payment_received`, payload)
  },

  updatePayment ({ commit }, { id, payload }) {
    return http.post(`payments/${id}`, payload)
      .then((response) => {
        commit(SET_PAYMENT_LIST_PAYMENT, response.data)

        return response
      })
  },

  setFilter ({ commit }, { filter, value }) {
    commit(SET_FILTER, { filter, value })
  },

  resetFilters ({ commit }) {
    commit(RESET_FILTERS)
  }
}

// Payment state mutations.
const mutations = {
  [SET_PAYMENTS] (state, payments) {
    state.payments = payments
  },

  [SET_PAYMENTS_PAGINATION] (state, pagination) {
    state.pagination = pick(pagination, ['current_page', 'per_page', 'total', 'last_page'])
  },

  [ADD_PAYMENTS] (state, payments) {
    state.payments = concat(state.payments, payments)
  },

  [SET_PAYMENT_CLIENT_TOKEN] (state, clientToken) {
    state.clientToken = clientToken
  },

  [SET_FILTER] (state, { filter, value }) {
    set(state.filters, filter, value)
  },

  [RESET_FILTERS] (state) {
    state.filters = initialPaymentFiltersState()
  },

  [SET_PAYMENT_LIST_PAYMENT] (state, payment) {
    let index = findIndex(state.payments, p => p.id === payment.id)

    if (index !== -1) {
      state.payments.splice(index, 1, payment)
    }
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations
}
